import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import AuthShell from 'components/layout/Auth/AuthShell';
import AddressSelectModal, {
  AddressOption,
} from 'components/Granel/AddressSelectModal';
import Stepper, { StepperStep } from 'design-system/Stepper';

import { useCountdown } from 'components/layout/Auth/useCountdown';
import Conclusao, { ValidacaoStatus } from './Conclusao';
import DadosPessoais from './DadosPessoais';
import Identificacao from './Identificacao';
import ModalOndeEncontrar from './ModalOndeEncontrar';
import Senha from './Senha';
import ValidarToken, { TokenStatus } from './ValidarToken';
import Validacao from './Validacao';
import { splitFullName } from './splitFullName';
import {
  createContact,
  createPassword,
  createUser,
  CustomerAddress,
  getCustomer,
  getShipToAddresses,
  parseError,
  sendValidationCode,
  validateRegistration,
} from './services';

const SEGUNDOS_DO_CODIGO = 60;

type Passo =
  | 'identificacao'
  | 'dados'
  | 'senha'
  | 'validacao'
  | 'codigo'
  | 'conclusao';

const ERRO_GENERICO = 'Ocorreu um erro interno na aplicação';

const SEM_CLIENTE =
  'Nenhum cliente encontrado. Verifique os dados digitados e tente novamente.';

const montarSteps = (
  passo: Passo,
  statusValidacao: ValidacaoStatus
): StepperStep[] => {
  const concluiuTudo = passo === 'conclusao' && statusValidacao === 'ok';

  const sobreVoce: StepperStep = {
    label: 'Etapa 1',
    name: 'Sobre você',
    status:
      passo === 'identificacao' || passo === 'dados'
        ? 'em-processo'
        : 'concluida',
    progress: passo === 'dados' ? 0.5 : 0,
  };

  const seguranca: StepperStep = {
    label: 'Etapa 2',
    name: 'Segurança',
    status:
      // eslint-disable-next-line no-nested-ternary
      passo === 'identificacao' || passo === 'dados'
        ? 'pendente'
        : passo === 'senha'
        ? 'em-processo'
        : 'concluida',
    progress: passo === 'senha' ? 0.5 : 0,
  };

  const validacao: StepperStep = {
    label: 'Etapa 3',
    name: 'Validação',
    // eslint-disable-next-line no-nested-ternary
    status: concluiuTudo
      ? 'concluida'
      : passo === 'validacao' || passo === 'codigo' || passo === 'conclusao'
      ? 'em-processo'
      : 'pendente',
  };

  return [sobreVoce, seguranca, validacao];
};

const descreverEndereco = (address: CustomerAddress): string =>
  [
    [address.addressLine, address.addressNumber].filter(Boolean).join(', '),
    address.complement,
    address.city,
    address.state,
  ]
    .filter(Boolean)
    .join(' - ');

const PrimeiroAcesso = (): JSX.Element => {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tokenDaUrl = params.get('token');
  const userIdDaUrl = params.get('user-id');
  const veioDoLink = !!tokenDaUrl && !!userIdDaUrl;

  const [passo, setPasso] = useState<Passo>(
    veioDoLink ? 'conclusao' : 'identificacao'
  );
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [customerCode, setCustomerCode] = useState('');
  const [document, setDocument] = useState('');
  const [corporateName, setCorporateName] = useState('');
  const [enderecos, setEnderecos] = useState<CustomerAddress[]>([]);
  const [siteId, setSiteId] = useState('');
  const [modalEnderecos, setModalEnderecos] = useState(false);
  const [modalAjuda, setModalAjuda] = useState(false);

  const [userId, setUserId] = useState(userIdDaUrl ?? '');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [phone, setPhone] = useState('');
  const [contactId, setContactId] = useState('');

  const [codigo, setCodigo] = useState('');
  const [statusToken, setStatusToken] = useState<TokenStatus>('aguardando');
  const { restante, expirado, restart } = useCountdown(SEGUNDOS_DO_CODIGO);
  const [statusValidacao, setStatusValidacao] = useState<ValidacaoStatus>(
    veioDoLink ? 'validando' : 'ok'
  );

  useEffect(() => {
    setErro(null);
  }, [passo]);

  const steps = useMemo(
    () => montarSteps(passo, statusValidacao),
    [passo, statusValidacao]
  );

  const opcoesEndereco: AddressOption[] = useMemo(
    () =>
      enderecos.map((address) => ({
        id: address.siteId,
        title: corporateName,
        description: descreverEndereco(address),
      })),
    [enderecos, corporateName]
  );

  const validarPeloLink = useCallback(async () => {
    setStatusValidacao('validando');
    setErro(null);
    try {
      await validateRegistration(userIdDaUrl as string, tokenDaUrl as string);
      setStatusValidacao('ok');
    } catch (error) {
      const { message } = parseError(error);
      setErro(message ?? ERRO_GENERICO);
      setStatusValidacao('erro');
    }
  }, [userIdDaUrl, tokenDaUrl]);

  useEffect(() => {
    if (veioDoLink) validarPeloLink();
  }, [veioDoLink, validarPeloLink]);

  const criarUsuario = useCallback(
    async (site: string) => {
      setLoading(true);
      setErro(null);
      try {
        const novoUserId = await createUser(document, customerCode, site);
        setUserId(novoUserId);
        setSiteId(site);
        setModalEnderecos(false);
        setPasso('dados');
      } catch (error) {
        const { message } = parseError(error);
        setErro(message ?? ERRO_GENERICO);
      }
      setLoading(false);
    },
    [document, customerCode]
  );

  const buscarCliente = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      const customer = await getCustomer(document, customerCode);

      if (!customer) throw new Error(SEM_CLIENTE);

      const shipTo = getShipToAddresses(customer);

      if (shipTo.length === 0) throw new Error(SEM_CLIENTE);

      setCorporateName(customer.corporateName);
      setEnderecos(shipTo);

      if (shipTo.length > 1) {
        setSiteId('');
        setModalEnderecos(true);
        setLoading(false);
        return;
      }

      await criarUsuario(shipTo[0].siteId);
      return;
    } catch (error) {
      const { message } = parseError(error);
      setErro(message ?? (error as Error).message ?? ERRO_GENERICO);
    }
    setLoading(false);
  }, [document, customerCode, criarUsuario]);

  const salvarContatos = useCallback(async () => {
    const { name, lastName } = splitFullName(fullName);

    setLoading(true);
    setErro(null);
    try {
      const idEmail = await createContact({
        userId,
        contact: email.trim(),
        contactType: 'EMAIL',
        document,
        customerCode,
        siteId,
        name,
        lastName,
      });

      await createContact({
        userId,
        contact: phone.replace(/\D/g, ''),
        contactType: 'PHONE',
        document,
        customerCode,
        siteId,
        name,
        lastName,
      });

      setContactId(idEmail);
      setPasso('senha');
    } catch (error) {
      const { message } = parseError(error);
      setErro(message ?? ERRO_GENERICO);
    }
    setLoading(false);
  }, [fullName, email, phone, userId, document, customerCode, siteId]);

  const salvarSenha = useCallback(
    async (senha: string) => {
      setLoading(true);
      setErro(null);
      try {
        await createPassword(userId, contactId, senha);
        setPasso('validacao');
      } catch (error) {
        const { message } = parseError(error);
        setErro(message ?? ERRO_GENERICO);
      }
      setLoading(false);
    },
    [userId, contactId]
  );

  const enviarCodigo = useCallback(async () => {
    setLoading(true);
    setErro(null);
    try {
      await sendValidationCode({
        userId,
        contactId,
        customerCode,
        document,
        email: email.trim(),
      });
      setCodigo('');
      setStatusToken('aguardando');
      restart();
      setPasso('codigo');
    } catch (error) {
      const { message } = parseError(error);
      setErro(message ?? ERRO_GENERICO);
    }
    setLoading(false);
  }, [userId, contactId, customerCode, document, email, restart]);

  const confirmarCodigo = useCallback(
    async (valor: string) => {
      setLoading(true);
      setErro(null);
      try {
        await validateRegistration(userId, valor);
        setStatusValidacao('ok');
        setPasso('conclusao');
      } catch (error) {
        const { message, status } = parseError(error);

        if (status === 401 || status === 422) {
          setStatusToken('invalido');
        } else {
          setErro(message ?? ERRO_GENERICO);
        }
      }
      setLoading(false);
    },
    [userId]
  );

  return (
    <AuthShell variant="onboarding">
      <Stepper steps={steps}>
        {passo === 'identificacao' && (
          <Identificacao
            customerCode={customerCode}
            document={document}
            loading={loading}
            erro={erro}
            onChangeCustomerCode={setCustomerCode}
            onChangeDocument={setDocument}
            onSubmit={buscarCliente}
            onHelp={() => setModalAjuda(true)}
          />
        )}

        {passo === 'dados' && (
          <DadosPessoais
            fullName={fullName}
            email={email}
            phone={phone}
            loading={loading}
            erro={erro}
            onChangeFullName={setFullName}
            onChangeEmail={setEmail}
            onChangePhone={setPhone}
            onSubmit={salvarContatos}
            onBack={() => setPasso('identificacao')}
          />
        )}

        {passo === 'senha' && (
          <Senha
            loading={loading}
            erro={erro}
            onSubmit={salvarSenha}
            onBack={() => setPasso('dados')}
          />
        )}

        {passo === 'validacao' && (
          <Validacao
            email={email}
            loading={loading}
            erro={erro}
            onSubmit={enviarCodigo}
            onBack={() => setPasso('senha')}
          />
        )}

        {passo === 'codigo' && (
          <ValidarToken
            codigo={codigo}
            status={statusToken}
            restante={restante}
            expirado={expirado}
            loading={loading}
            erro={erro}
            onChangeCodigo={(valor) => {
              setCodigo(valor);
              setStatusToken('aguardando');
            }}
            onSubmit={confirmarCodigo}
            onReenviar={enviarCodigo}
            onBack={() => setPasso('validacao')}
          />
        )}

        {passo === 'conclusao' && (
          <Conclusao
            status={statusValidacao}
            erro={erro}
            onRetry={validarPeloLink}
          />
        )}
      </Stepper>

      <AddressSelectModal
        open={modalEnderecos}
        addresses={opcoesEndereco}
        value={siteId}
        loading={loading}
        onChange={setSiteId}
        onConfirm={() => criarUsuario(siteId)}
        onClose={() => setModalEnderecos(false)}
      />

      <ModalOndeEncontrar
        open={modalAjuda}
        onClose={() => setModalAjuda(false)}
      />
    </AuthShell>
  );
};

export default PrimeiroAcesso;
